import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useFormikContext } from 'formik';
import { Lock1 } from 'iconsax-react';
import {
  type MutableRefObject,
  type ReactElement,
  createElement,
  isValidElement,
  useCallback,
  useContext,
} from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { QuoteFormikType, QuoteSubmitType, RequiredQuoteFieldsInfo } from '@core/formik/quote';
import Enums from '@core/types/api/Enums';
import { hasFormikFieldsErrors } from '@core/utils/formikHelpers';
import { isValidUUID } from '@core/utils/uuid';
import type { FileUploadsManager } from '@web/components/Files';
import type { ProductSectionProps } from '@web/components/ProductSection/ProductSection';
import { SlabDrawerHeader } from '@web/components/SlabDrawer/SlabDrawerHeader';
import { SlabStepper } from '@web/components/SlabStepper/SlabStepper';
import { useStepper } from '@web/components/SlabStepper/useStepper';
import { ViewOnlyFields } from '@web/components/ViewOnlyFields/ViewOnlyFields';

import { QuoteDrawerMode, useQuoteDrawerContext } from '../QuoteDrawer';
import { usePlantDistanceCalculationForQuotes } from '../usePlantDistanceCalculationForQuotes';
import { AdditionalServicesSection } from './AdditionalServicesSection';
import { BuyerInformationSection } from './BuyerInformationSection';
import { DispatchProductRestrictionsSection } from './DispatchProductRestrictionsSection';
import { NotesSection } from './NotesSection';
import { PriceEscalationSection } from './PriceEscalationSection';
import { ProductsSection } from './ProductsSection';
import { QuoteFilesSection } from './QuoteFilesSection';
import { QuoteInformationSection } from './QuoteInformationSection';
import { TermsSection } from './TermsSection';
import { useBuyerPricingReconciler } from './useBuyerPricingReconciler';

export enum QuoteSection {
  QUOTE_INFORMATION = 'Quote Information*',
  BUYER_INFORMATION = 'Buyer Information',
  PRODUCTS = 'Products',
  ADDITIONAL_SERVICES = 'Additional services',
  DISPATCH_PRODUCT_RESTRICTIONS = 'Dispatch Product Restrictions',
  PRICE_ESCALATIONS = 'Price Escalations',
  TERMS = 'Terms/T&C (Appears on Quote)',
  NOTES = 'Notes (Internal Only)',
  FILES = 'Files',
}

type QuoteDrawerSectionsProps = {
  initialSection: QuoteSection;
  curProductsRef: ProductSectionProps['curProductsRef'];
  submitType: QuoteSubmitType;
  requiredFields: RequiredQuoteFieldsInfo;
  closeDrawer: () => Promise<void> | void;
  mode: QuoteDrawerMode;
  filesManager: FileUploadsManager;
  isViewOnly: boolean;
  openedFromProject?: boolean;
  hasSeededFromProjectRef: MutableRefObject<boolean>;
  pendingProjectChangeRef: MutableRefObject<boolean>;
};

export const QuoteDrawerSections = ({
  initialSection,
  curProductsRef,
  submitType,
  requiredFields,
  closeDrawer,
  mode,
  filesManager,
  isViewOnly,
  openedFromProject = false,
  hasSeededFromProjectRef,
  pendingProjectChangeRef,
}: QuoteDrawerSectionsProps): ReactElement => {
  const formikBag = useFormikContext<QuoteFormikType>();
  const { readOnly } = useQuoteDrawerContext();
  const theme = useTheme();

  // Calculate plant distances and drive times when project address or plants change
  usePlantDistanceCalculationForQuotes();

  const reconcileBuyerPricing = useBuyerPricingReconciler(curProductsRef);

  const QuoteInformation = useCallback(
    () => (
      <QuoteInformationSection
        curProductsRef={curProductsRef}
        requiredFields={requiredFields}
        mode={mode}
        submitType={submitType}
        reconcileBuyerPricing={reconcileBuyerPricing}
        openedFromProject={openedFromProject}
        hasSeededFromProjectRef={hasSeededFromProjectRef}
        pendingProjectChangeRef={pendingProjectChangeRef}
      />
    ),
    [
      curProductsRef,
      mode,
      submitType,
      reconcileBuyerPricing,
      openedFromProject,
      hasSeededFromProjectRef,
      pendingProjectChangeRef,
    ],
  );

  const Products = useCallback(
    () => <ProductsSection curProductsRef={curProductsRef} mode={mode} />,
    [curProductsRef, mode],
  );

  const AdditionalServices = useCallback(
    () => <AdditionalServicesSection curProductsRef={curProductsRef} />,
    [curProductsRef],
  );

  const BuyerInformation = useCallback(
    () => (
      <BuyerInformationSection
        submitType={submitType}
        requiredFields={requiredFields}
        reconcileBuyerPricing={reconcileBuyerPricing}
      />
    ),
    [submitType, requiredFields, reconcileBuyerPricing],
  );

  // Build steps array conditionally
  const ctx = useContext(SlabContext);
  const hasDispatchIntegration = ctx.userInfo.hasFlags(
    [
      Enums.FeatureFlagName.FeatureFlagIntegrationExport,
      Enums.FeatureFlagName.FeatureFlagIntegrationRestrictOrders,
    ],
    'and',
  );
  const filesFlagEnabled = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagFileAttachments,
  ]);

  const quoteId = formikBag.values.id;
  const isNewQuote = !isValidUUID(quoteId);
  const Files = useCallback(
    () => <QuoteFilesSection quoteID={isNewQuote ? null : quoteId} manager={filesManager} />,
    [isNewQuote, quoteId, filesManager],
  );

  const baseSteps = [
    {
      hidden: readOnly && initialSection !== QuoteSection.QUOTE_INFORMATION,
      label: QuoteSection.QUOTE_INFORMATION,
      content: QuoteInformation,
      error:
        formikBag.submitCount > 0 &&
        hasFormikFieldsErrors(formikBag, ['status', 'user', 'project', 'name']),
    },
    {
      hidden: readOnly && initialSection !== QuoteSection.BUYER_INFORMATION,
      label: QuoteSection.BUYER_INFORMATION,
      disabled: formikBag.values.project.id === null,
      content: BuyerInformation,
      error: formikBag.submitCount > 0 && hasFormikFieldsErrors(formikBag, ['contacts', 'company']),
    },
    {
      hidden: readOnly && initialSection !== QuoteSection.PRODUCTS,
      label: QuoteSection.PRODUCTS,
      disabled: formikBag.values.project.id === null,
      content: Products,
      error:
        formikBag.submitCount > 0 && hasFormikFieldsErrors(formikBag, ['mainProductsByPlants']),
    },
    {
      hidden: readOnly && initialSection !== QuoteSection.ADDITIONAL_SERVICES,
      label: QuoteSection.ADDITIONAL_SERVICES,
      disabled: formikBag.values.project.id === null,
      content: AdditionalServices,
      error:
        formikBag.submitCount > 0 &&
        hasFormikFieldsErrors(formikBag, ['additionalProductsByPlants']),
    },
  ];

  // Conditionally add dispatch product restrictions section
  if (hasDispatchIntegration) {
    baseSteps.push({
      hidden: readOnly && initialSection !== QuoteSection.DISPATCH_PRODUCT_RESTRICTIONS,
      label: QuoteSection.DISPATCH_PRODUCT_RESTRICTIONS,
      content: DispatchProductRestrictionsSection,
      error:
        formikBag.submitCount > 0 &&
        hasFormikFieldsErrors(formikBag, [
          'restrictOrdersToProjectProducts',
          'restrictOrdersToProjectMixProducts',
        ]),
    });
  }

  // Add remaining sections
  baseSteps.push(
    {
      hidden: readOnly && initialSection !== QuoteSection.PRICE_ESCALATIONS,
      label: QuoteSection.PRICE_ESCALATIONS,
      content: PriceEscalationSection,
      error: formikBag.submitCount > 0 && hasFormikFieldsErrors(formikBag, ['priceEscalations']),
    },
    {
      hidden: readOnly && initialSection !== QuoteSection.TERMS,
      label: QuoteSection.TERMS,
      content: TermsSection,
      error: formikBag.submitCount > 0 && hasFormikFieldsErrors(formikBag, ['terms']),
    },
    {
      hidden: readOnly && initialSection !== QuoteSection.NOTES,
      label: QuoteSection.NOTES,
      content: NotesSection,
      error: formikBag.submitCount > 0 && hasFormikFieldsErrors(formikBag, ['notes']),
    },
    {
      hidden: !filesFlagEnabled,
      label: QuoteSection.FILES,
      content: Files,
      // No Formik field backs this step (uploads aren't form state), so it never
      // shows the validation-error indicator the other steps derive from
      // hasFormikFieldsErrors.
      error: false,
    },
  );

  const filteredSteps =
    mode === QuoteDrawerMode.Quick
      ? baseSteps
          .filter(
            (step) =>
              step.label === QuoteSection.QUOTE_INFORMATION || step.label === QuoteSection.PRODUCTS,
          )
          .map((step) =>
            step.label === QuoteSection.PRODUCTS ? { ...step, disabled: false } : step,
          )
      : baseSteps;

  const viewOnlySteps = isViewOnly
    ? filteredSteps.map(({ content, ...step }) => ({
        ...step,
        content: (
          <ViewOnlyFields>
            {isValidElement(content) ? content : createElement(content)}
          </ViewOnlyFields>
        ),
      }))
    : filteredSteps;

  const { steps, activeStep, changeStep } = useStepper({
    initialStep: initialSection,
    steps: viewOnlySteps,
  });

  if (readOnly) {
    const targetSection = filteredSteps.find((step) => step.label === initialSection);
    const Content = targetSection?.content;
    return (
      <Box mt={2}>
        <SlabDrawerHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Lock1 size={24} />
              <Typography variant='h1' fontSize='2rem'>
                View {targetSection?.label}
              </Typography>
            </Box>
          }
          onClose={closeDrawer}
        />

        <Typography mt={1} variant='body2' color={theme.palette.SlabGray[600]}>
          Read-only mode - Create a new revision to edit this quote
        </Typography>

        {Content && <Content />}
      </Box>
    );
  }

  return <SlabStepper onChangeStep={changeStep} steps={steps} activeStep={activeStep} />;
};
